import type { FC } from 'react';
import { useState, useId, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { MAX_TAG_COUNT } from '@wix/da-ds/pkg/formControls/lego/TagInputField/helpers';
import TagInput from '@wix/da-shared-react/pkg/TagInput';
import { Props as TagInputProps } from '@wix/da-shared-react/pkg/TagInput/TagInput';
import { BatchEditTagsOperation } from '../../../types/batchActions';
import DesktopBatchActionWrapper from '../DesktopBatchActionWrapper';
import MobileBatchActionWrapper from '../MobileBatchActionWrapper';
import { BatchActionProps } from '../types';

import s from './TagsBatchEdit.scss';

export interface Props extends BatchActionProps {
  batchEditTags: (operation: BatchEditTagsOperation, tags: string[]) => void;
}

const TagsBatchEdit: FC<Props> = ({ selectionArea, batchEditTags }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [operation, setOperation] = useState<BatchEditTagsOperation>('add');
  const [tags, setTags] = useState<string[]>([]);
  const operationInputId = useId();
  const tagInputId = useId();

  const { errorMessage, validationResult } = validateTags(tags, operation, t);
  const disableSave = validationResult === 'error';

  const handleSave = useCallback(() => {
    batchEditTags(operation, tags);
  }, [batchEditTags, operation, tags]);

  const form = (
    <>
      <p className={s['input-description']}>
        {t('studio.batchActions.form.edit_tags.tags.inputDescription', {
          max: MAX_TAG_COUNT,
        })}
      </p>

      <div className={s['form']}>
        <SelectField
          className={s['operation-field']}
          id={operationInputId}
          items={[
            {
              value: 'add',
              label: t('studio.batchActions.form.edit_tags.operations.add'),
            },
            {
              value: 'remove',
              label: t('studio.batchActions.form.edit_tags.operations.remove'),
            },
            {
              value: 'replace',
              label: t('studio.batchActions.form.edit_tags.operations.replace'),
            },
          ]}
          onSelect={value => setOperation(value as BatchEditTagsOperation)}
          value={operation}
          label="Operation"
          isLabelHidden
          withBlankOption={false}
        />

        <TagInput
          className={s['tags-field']}
          id={tagInputId}
          isLabelHidden
          label={t('studio.batchActions.form.edit_tags.tags.label')}
          placeholder={t('studio.batchActions.form.edit_tags.tags.placeholder')}
          value={tags}
          onChange={value => setTags(value)}
          validationResult={validationResult}
          details={errorMessage}
          isTruncated
        />
      </div>
    </>
  );

  if (isMobile) {
    return (
      <MobileBatchActionWrapper
        disableSave={disableSave}
        onSave={handleSave}
        restrictSave="non_core_multiple"
      >
        {form}
      </MobileBatchActionWrapper>
    );
  }

  return (
    <DesktopBatchActionWrapper
      selectionArea={selectionArea}
      disableSave={disableSave}
      onSave={handleSave}
      restrictSave="non_core_multiple"
    >
      {form}
    </DesktopBatchActionWrapper>
  );
};
TagsBatchEdit.displayName = 'TagsBatchEdit';

export default TagsBatchEdit;

/** return a localized error message and error/success result based on tags
 *  and operation requested */
function validateTags(
  tags: string[],
  operation: BatchEditTagsOperation,
  t: Function
): {
  errorMessage?: string;
  validationResult?: TagInputProps['validationResult'];
} {
  if (['add', 'replace'].includes(operation) && tags.length > MAX_TAG_COUNT) {
    return {
      // shouldn't need singular/plural
      errorMessage: t('studio.batchActions.form.edit_tags.tags.error.tooMany', {
        max: MAX_TAG_COUNT,
      }),
      validationResult: 'error',
    };
  }
  return {};
}
